import { ITableFilter } from "../models/components/table/table-filter.model";
import { TableFilterType } from "../models/components/table/table-filter.model";
import { AlertCategory, AlertCategoryMapping } from "../models/entities/alert-category.model";

/**
 * Builds RSQL filter entries for reason, (optionally) details, and any backend-level
 * category.filters constraints from a category's mapping.
 * When a tab is selected, these are merged into the pagination filters so the alerts list/count use RSQL only (no categoryId).
 */
export function getFiltersForCategory(category: AlertCategory): ITableFilter[] {
	const reasons = Array.from(
		new Set(category.mapping.map((m: AlertCategoryMapping) => m.reason))
	);
	const detailsList = category.mapping
		.flatMap((m: AlertCategoryMapping) => m.details ?? [])
		.filter(Boolean);
	const details = Array.from(new Set(detailsList));

	const result: ITableFilter[] = [
		{
			field: "reason",
			type: TableFilterType.SELECT_MULTIPLE,
			value: reasons,
		},
	];
	if (details.length > 0) {
		result.push({
			field: "details",
			type: TableFilterType.SELECT_MULTIPLE,
			value: details,
		});
	}

	// Add any backend-level constraints defined on the category itself
	if (category.filters) {
		Object.entries(category.filters).forEach(([field, values]) => {
			if (values.length > 0) {
				result.push({
					field,
					type: TableFilterType.SELECT_MULTIPLE,
					value: values,
				});
			}
		});
	}

	return result;
}

/**
 * Merges base filters with category filters.
 * - reason/details: user-selected values are intersected with category values (AND semantics)
 * - category.filters fields: appended as-is (backend-level constraints, not user-selectable)
 */
export function mergeCategoryIntoFilters(
	baseFilters: ITableFilter[],
	category: AlertCategory | null
): ITableFilter[] {
	const categoryFilterFields = category?.filters ? Object.keys(category.filters) : [];

	// Strip reason, details, and any fields owned by the category's filters constraint
	const withoutCategoryFields = baseFilters.filter(
		(f) =>
			f.field !== "reason" && f.field !== "details" && !categoryFilterFields.includes(f.field)
	);

	if (!category) return withoutCategoryFields;

	const categoryFilters = getFiltersForCategory(category);
	const baseReason = baseFilters.find((f) => f.field === "reason");
	const baseDetails = baseFilters.find((f) => f.field === "details");
	const categoryReason = categoryFilters.find((f) => f.field === "reason");
	const categoryDetails = categoryFilters.find((f) => f.field === "details");

	const baseReasonValues = Array.isArray(baseReason?.value) ? baseReason!.value : [];
	const categoryReasonValues = Array.isArray(categoryReason?.value) ? categoryReason!.value : [];

	const baseDetailsValues = Array.isArray(baseDetails?.value) ? baseDetails!.value : [];
	const categoryDetailsValues = Array.isArray(categoryDetails?.value)
		? categoryDetails!.value
		: [];

	const merged: ITableFilter[] = [...withoutCategoryFields];

	if (categoryReason) {
		merged.push({
			field: "reason",
			type: categoryReason.type ?? TableFilterType.SELECT_MULTIPLE,
			value: baseReasonValues,
		});
		merged.push({
			field: "reason",
			type: categoryReason.type ?? TableFilterType.SELECT_MULTIPLE,
			value: categoryReasonValues,
		});
	}
	if (categoryDetails) {
		merged.push({
			field: "details",
			type: categoryDetails.type ?? TableFilterType.SELECT_MULTIPLE,
			value: baseDetailsValues,
		});
		merged.push({
			field: "details",
			type: categoryDetails.type ?? TableFilterType.SELECT_MULTIPLE,
			value: categoryDetailsValues,
		});
	}

	// Append backend-level category constraints directly (no user intersection)
	const categoryConstraints = categoryFilters.filter(
		(f) => f.field !== "reason" && f.field !== "details"
	);
	merged.push(...categoryConstraints);

	return merged;
}
